import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getWorkoutPrograms, getWorkoutCategories, deleteWorkoutCategory, getCombinedWorkouts, deleteWorkoutProgram, createWorkoutProgram } from '../../services/programService';
import { Program } from '../../types/program';

interface ProgramState {
    programs: Program[];
    categories: Program[];
    currentPage: number;
    totalPages: number;
    totalItems: number;
    loading: boolean;
    error: string | null;
}

const initialState: ProgramState = {
    programs: [],
    categories: [],
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    loading: false,
    error: null,
};

export const fetchWorkoutPrograms = createAsyncThunk(
    'programs/fetchPrograms',
    async ({ page, limit }: { page: number; limit: number }) => {
        return await getWorkoutPrograms(page, limit);
    }
);

export const fetchWorkoutCategories = createAsyncThunk(
    'programs/fetchCategories',
    async ({ page, limit }: { page: number; limit: number }) => {
        return await getWorkoutCategories(page, limit);
    }
);

export const removeWorkoutProgram = createAsyncThunk(
    'programs/removeProgram',
    async (programId: number) => {
        await deleteWorkoutProgram(programId);
        return programId;
    }
);

export const removeWorkoutCategory = createAsyncThunk(
    'programs/removeCategory',
    async (categoryId: number) => {
        await deleteWorkoutCategory(categoryId);
        return categoryId;
    }
);

export const createNewWorkoutProgram = createAsyncThunk(
    'programs/createProgram',
    async (program: { title: string; imageUrl: string; trainerImage: string | null; trainer: string }) => {
        return await createWorkoutProgram(program);
    }
);

export const fetchCombinedWorkouts = createAsyncThunk(
    'programs/fetchCombinedWorkouts',
    async ({ page, limit }: { page: number; limit: number }) => {
        console.log('Fetching combined workouts');

        return await getCombinedWorkouts(page, limit);
    }
);

const programSlice = createSlice({
    name: 'programs',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkoutPrograms.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkoutPrograms.fulfilled, (state, action) => {
                state.loading = false;
                state.programs = action.payload.data;
                state.totalPages = action.payload.totalPages;
                state.totalItems = action.payload.total;
                state.currentPage = action.payload.page;
            })
            .addCase(fetchWorkoutPrograms.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch programs';
            })
            .addCase(fetchWorkoutCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkoutCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload.data;
                state.totalPages = action.payload.totalPages;
                state.totalItems = action.payload.total;
                state.currentPage = action.payload.page;
            })
            .addCase(fetchWorkoutCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch categories';
            })
            .addCase(removeWorkoutProgram.fulfilled, (state, action) => {
                state.programs = state.programs.filter((program) => program.id !== action.payload);
            })
            .addCase(removeWorkoutCategory.fulfilled, (state, action) => {
                state.categories = state.categories.filter((category) => category.id !== action.payload);
            })
            .addCase(createNewWorkoutProgram.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createNewWorkoutProgram.fulfilled, (state, action) => {
                state.loading = false;
                state.programs.push(action.payload);
            })
            .addCase(createNewWorkoutProgram.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create program';
            })
    },
});

export default programSlice.reducer;
